import React, { useState } from 'react'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import PropTypes from 'prop-types'

// Components
import { Button } from '../Button'
import { DataTableMultiSelect } from '../DataTableMultiSelect'
import { DataTableRadioGroup } from '../DataTableRadioGroup'
import { StatusTag } from '../StatusTag'

// Utils
import { percentToDecimal } from '../../utils/helpers'

dayjs.extend(utc)
dayjs.extend(timezone)

export const TextAreaEditor = (options) => (
  <div className="inline-flex w-full">
    <InputTextarea
      value={options.value || ''}
      onChange={(e) => options.editorCallback(e.target.value)}
      cols={30}
      rows={1}
      className="max-h-[76px] min-h-[40px] w-full"
      autoResize
    />
  </div>
)

export const TextEditor = (options) => (
  <div className="inline-flex w-full">
    <InputText
      value={options.value}
      onChange={(e) => options.editorCallback(e.target.value)}
      className="w-full"
    />
  </div>
)

export const DateEditor = (options) => {
  // Parse the date to ensure it's treated as UTC
  const dateValue = options.value ? dayjs(options.value, 'MM/DD/YYYY').toDate() : null

  return (
    <Calendar
      value={dateValue}
      onChange={(e) => options.editorCallback(e.value)}
      dateFormat="mm/dd/yy"
      placeholder="Select a date"
    />
  )
}

export const PercentageEditor = (options) => {
  // Preserve 0 — truthiness would render 0% as a blank input.
  const percentageValue =
    options.value !== null && options.value !== undefined && !Number.isNaN(options.value)
      ? parseFloat(options.value * 100)
      : null
  const handleValueChange = (e) => {
    // Convert the input back to a decimal before sending to the BE. Use
    // percentToDecimal to avoid IEEE-754 noise (e.g. 0.35 / 100 =
    // 0.0035000000000000005) that trips the API's 18-decimal validator.
    options.editorCallback(percentToDecimal(e.value))
  }

  return (
    <InputNumber
      allowEmpty
      value={percentageValue}
      onValueChange={handleValueChange}
      mode="decimal"
      max="100"
      minFractionDigits={1}
      maxFractionDigits={5}
      suffix="%"
    />
  )
}

export const PriceEditor = (options) => (
  <InputNumber
    value={options.value}
    onValueChange={(e) => options.editorCallback(e.value)}
    mode="currency"
    currency="USD"
    locale="en-US"
  />
)

export const NumberEditor = (options) => (
  <InputNumber
    value={options.value}
    onValueChange={(e) => options.editorCallback(e.value)}
    mode="decimal"
  />
)

const StatusItemTemplate = (option) => (
  <StatusTag value={option.label} severity={option.value ? 'success' : 'danger'} />
)

export const StatusEditor = (options) => (
  <Dropdown
    value={options.value}
    options={[
      { label: 'Yes', value: true },
      { label: 'No', value: false },
    ]}
    onChange={(e) => options.editorCallback(e.value)}
    placeholder="Select a Status"
    optionLabel="label"
    itemTemplate={StatusItemTemplate}
  />
)

export const SelectEditor = (options) => (
  <Dropdown
    value={options.value}
    options={options.options}
    onChange={(e) => options.editorCallback(e.value)}
    placeholder={options.placeholder}
    optionLabel="label"
    pt={{
      root: 'md:w-full',
    }}
  />
)

export const renderSelectEditor = (options, value, placeholder, rest) => (
  <SelectEditor {...rest} value={value} options={options} placeholder={placeholder} />
)

export const renderMultiSelectFilter = (options, handleChange, placeholder, rest) => (
  <DataTableMultiSelect
    {...rest}
    data={options}
    handleChange={handleChange}
    placeholder={placeholder}
    search
  />
)

export const renderRadioFilter = (options, handleChange, placeholder, rest) => (
  <DataTableRadioGroup
    {...rest}
    options={options}
    handleChange={handleChange}
    placeholder={placeholder}
  />
)

export const renderStatusTag = (data) => (
  <StatusTag value={data ? 'Yes' : 'No'} severity={data ? 'success' : 'danger'} />
)

export const CustomTextFilter = ({ options, handleChange, type = 'String' }) => {
  // State
  const [value, setValue] = useState(options.value || '')

  return (
    <div className="flex w-full flex-col gap-2 px-2">
      {type === 'Number' ? (
        <InputNumber
          value={options.value}
          onValueChange={(e) => setValue(e.value)}
          mode="decimal"
        />
      ) : (
        <InputText value={value} onChange={(e) => setValue(e.target.value)} className="w-full" />
      )}

      <div className="flex w-full flex-row gap-2">
        {value && (
          <Button
            background="bg-gray"
            className="w-full"
            label="Clear"
            onClick={() => {
              setValue('')
              handleChange('')
            }}
          />
        )}

        <Button className="w-full" label="Apply" onClick={() => handleChange(value)} />
      </div>
    </div>
  )
}

CustomTextFilter.propTypes = {
  options: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  type: PropTypes.string,
}
